import { useState, useCallback, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { notionIntegrationService } from '@/services/notion-integration'
import type { NotionPageItem } from '@/schemas/notion-sync'
import {
  FileText,
  Database,
  ChevronRight,
  ChevronDown,
  Loader2,
  AlertCircle,
  Search,
  X,
} from 'lucide-react'

interface SelectedPage {
  id: string
  title: string
}

interface NotionPagePickerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  organizationId: string
  selectedPages: SelectedPage[]
  onSelect: (pages: SelectedPage[]) => void
}

interface TreeNode extends NotionPageItem {
  children?: TreeNode[]
  isLoading?: boolean
  isExpanded?: boolean
}

export function NotionPagePicker({
  open,
  onOpenChange,
  organizationId,
  selectedPages,
  onSelect,
}: NotionPagePickerProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pages, setPages] = useState<TreeNode[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<NotionPageItem[]>([])
  const [localSelected, setLocalSelected] = useState<SelectedPage[]>(selectedPages)

  // Fetch root pages on mount
  const fetchRootPages = useCallback(async () => {
    if (!organizationId) return

    try {
      setIsLoading(true)
      setError(null)
      const response = await notionIntegrationService.searchPages(organizationId)
      setPages(response.pages.map((p) => ({ ...p, isExpanded: false })))
    } catch (err) {
      console.error('Failed to fetch pages:', err)
      setError('Erro ao carregar paginas do Notion')
    } finally {
      setIsLoading(false)
    }
  }, [organizationId])

  // Load children when expanding a node
  const loadChildren = useCallback(
    async (pageId: string) => {
      if (!organizationId) return

      // Find and mark the node as loading
      setPages((prev) =>
        updateNode(prev, pageId, { isLoading: true })
      )

      try {
        const response = await notionIntegrationService.listPageChildren(
          organizationId,
          pageId
        )
        setPages((prev) =>
          updateNode(prev, pageId, {
            isLoading: false,
            isExpanded: true,
            children: response.pages.map((p) => ({ ...p, isExpanded: false })),
          })
        )
      } catch (err) {
        console.error('Failed to fetch children:', err)
        setPages((prev) =>
          updateNode(prev, pageId, { isLoading: false })
        )
      }
    },
    [organizationId]
  )

  // Search pages
  const searchPages = useCallback(
    async (query: string) => {
      if (!organizationId || !query.trim()) {
        setSearchResults([])
        return
      }

      try {
        setIsSearching(true)
        const response = await notionIntegrationService.searchPages(
          organizationId,
          query
        )
        setSearchResults(response.pages)
      } catch (err) {
        console.error('Failed to search pages:', err)
      } finally {
        setIsSearching(false)
      }
    },
    [organizationId]
  )

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        searchPages(searchQuery)
      } else {
        setSearchResults([])
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery, searchPages])

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSearchQuery('')
      setSearchResults([])
      setLocalSelected(selectedPages)
      setError(null)
      fetchRootPages()
    }
  }, [open, selectedPages, fetchRootPages])

  const handleToggleExpand = (page: TreeNode) => {
    if (page.isExpanded) {
      // Collapse
      setPages((prev) => updateNode(prev, page.id, { isExpanded: false }))
    } else if (page.has_children) {
      // Expand and load children if not already loaded
      if (!page.children) {
        loadChildren(page.id)
      } else {
        setPages((prev) => updateNode(prev, page.id, { isExpanded: true }))
      }
    }
  }

  const handleToggleSelect = (page: NotionPageItem) => {
    setLocalSelected((prev) => {
      const isSelected = prev.some((p) => p.id === page.id)
      if (isSelected) {
        return prev.filter((p) => p.id !== page.id)
      } else {
        return [...prev, { id: page.id, title: page.title }]
      }
    })
  }

  const handleConfirm = () => {
    onSelect(localSelected)
    onOpenChange(false)
  }

  const handleClearSelection = () => {
    setLocalSelected([])
  }

  const isSelected = (pageId: string) => localSelected.some((p) => p.id === pageId)

  const getPageIcon = (page: NotionPageItem) => {
    if (page.icon) {
      return <span className="text-base">{page.icon}</span>
    }
    if (page.type === 'database') {
      return <Database className="h-4 w-4 text-blue-400" />
    }
    return <FileText className="h-4 w-4 text-gray-400" />
  }

  const renderPageItem = (page: TreeNode, depth: number = 0) => {
    const paddingLeft = 12 + depth * 20

    return (
      <div key={page.id}>
        <div
          className={`flex items-center gap-2 px-2 py-2 hover:bg-gray-800/50 transition-colors cursor-pointer ${
            isSelected(page.id) ? 'bg-emerald-500/10' : ''
          }`}
          style={{ paddingLeft }}
        >
          {/* Expand/Collapse button */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleToggleExpand(page)
            }}
            className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-300"
            disabled={!page.has_children}
          >
            {page.isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : page.has_children ? (
              page.isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )
            ) : null}
          </button>

          {/* Checkbox */}
          <Checkbox
            checked={isSelected(page.id)}
            onCheckedChange={() => handleToggleSelect(page)}
            className="border-gray-500"
          />

          {/* Icon */}
          <div className="flex-shrink-0">{getPageIcon(page)}</div>

          {/* Title */}
          <span
            className="flex-1 text-sm text-gray-200 truncate"
            onClick={() => handleToggleSelect(page)}
          >
            {page.title || 'Sem titulo'}
          </span>

          {/* Type badge */}
          {page.type === 'database' && (
            <Badge variant="outline" className="text-xs border-blue-500/30 text-blue-400">
              Database
            </Badge>
          )}
        </div>

        {/* Children */}
        {page.isExpanded && page.children && (
          <div>
            {page.children.map((child) => renderPageItem(child, depth + 1))}
          </div>
        )}
      </div>
    )
  }

  const renderSearchResult = (page: NotionPageItem) => (
    <div
      key={page.id}
      className={`flex items-center gap-2 px-3 py-2 hover:bg-gray-800/50 transition-colors cursor-pointer ${
        isSelected(page.id) ? 'bg-emerald-500/10' : ''
      }`}
      onClick={() => handleToggleSelect(page)}
    >
      <Checkbox
        checked={isSelected(page.id)}
        onCheckedChange={() => handleToggleSelect(page)}
        className="border-gray-500"
      />
      <div className="flex-shrink-0">{getPageIcon(page)}</div>
      <span className="flex-1 text-sm text-gray-200 truncate">
        {page.title || 'Sem titulo'}
      </span>
      {page.type === 'database' && (
        <Badge variant="outline" className="text-xs border-blue-500/30 text-blue-400">
          Database
        </Badge>
      )}
    </div>
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-gray-900 border-gray-700">
        <DialogHeader>
          <DialogTitle className="text-white">Selecionar Paginas do Notion</DialogTitle>
          <DialogDescription className="text-gray-400">
            Selecione as paginas que deseja sincronizar com a base de conhecimento.
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar paginas..."
            className="pl-10 bg-gray-800 border-gray-700"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-300"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Selected count */}
        {localSelected.length > 0 && (
          <div className="flex items-center justify-between px-3 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-md">
            <span className="text-sm text-emerald-400">
              {localSelected.length} pagina{localSelected.length !== 1 ? 's' : ''} selecionada{localSelected.length !== 1 ? 's' : ''}
            </span>
            <button
              onClick={handleClearSelection}
              className="text-xs text-gray-400 hover:text-gray-200"
            >
              Limpar selecao
            </button>
          </div>
        )}

        {/* Page list */}
        <div className="min-h-[300px] max-h-[400px] overflow-y-auto border border-gray-700 rounded-md">
          {isLoading || isSearching ? (
            <div className="flex items-center justify-center h-full py-12">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full py-12 text-center">
              <AlertCircle className="h-8 w-8 text-red-500 mb-2" />
              <p className="text-red-400 text-sm">{error}</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchRootPages}
                className="mt-2"
              >
                Tentar novamente
              </Button>
            </div>
          ) : searchQuery ? (
            // Search results
            searchResults.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                <Search className="h-8 w-8 text-gray-500 mb-2" />
                <p className="text-gray-400 text-sm">Nenhuma pagina encontrada</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-700/50">
                {searchResults.map(renderSearchResult)}
              </div>
            )
          ) : pages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-12 text-center">
              <FileText className="h-8 w-8 text-gray-500 mb-2" />
              <p className="text-gray-400 text-sm">Nenhuma pagina encontrada</p>
              <p className="text-gray-500 text-xs mt-1">
                Verifique as permissoes de acesso no Notion.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-700/50">
              {pages.map((page) => renderPageItem(page))}
            </div>
          )}
        </div>

        {/* Selected pages preview */}
        {localSelected.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs text-gray-400">Paginas selecionadas:</p>
            <div className="flex flex-wrap gap-1">
              {localSelected.slice(0, 5).map((page) => (
                <Badge
                  key={page.id}
                  variant="secondary"
                  className="text-xs bg-gray-800"
                >
                  {page.title}
                  <button
                    onClick={() => handleToggleSelect({ id: page.id, title: page.title } as NotionPageItem)}
                    className="ml-1 text-gray-400 hover:text-gray-200"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              {localSelected.length > 5 && (
                <Badge variant="secondary" className="text-xs bg-gray-800">
                  +{localSelected.length - 5}
                </Badge>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-gray-400"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={localSelected.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            Selecionar ({localSelected.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Helper function to update a node in the tree
function updateNode(
  nodes: TreeNode[],
  id: string,
  updates: Partial<TreeNode>
): TreeNode[] {
  return nodes.map((node) => {
    if (node.id === id) {
      return { ...node, ...updates }
    }
    if (node.children) {
      return { ...node, children: updateNode(node.children, id, updates) }
    }
    return node
  })
}
